/**
 * Community service
 * Handles feedback, reports, document submissions, and notifications
 */

import api from "../api";

// ─── Types ────────────────────────────────────────────────────────────────────

export type FeedbackType = "feature_request" | "bug_report";

export type ReportTargetType = "comment" | "document" | "anonhandle";

export type ReportReason =
  | "spam"
  | "harassment"
  | "misinformation"
  | "impersonation"
  | "inappropriate"
  | "other";

export type NotificationType =
  | "report_resolved"
  | "submission_approved"
  | "submission_rejected"
  | "feedback_acknowledged"
  | "feedback_resolved"
  | "comment_on_doc"
  | "comment_vote"
  | "new_follower";

export interface FeedbackPayload {
  type: FeedbackType;
  title: string;
  body: string;
}

export interface ReportPayload {
  target_type: ReportTargetType;
  target_id?: string;
  reason: ReportReason;
  details?: string;
}

export interface Notification {
  id: string;
  type: NotificationType;
  type_display: string;
  title: string;
  body: string;
  is_read: boolean;
  related_type: string;
  related_id: string | null;
  created_at: string;
}

export const SUBMISSION_CATEGORIES = [
  "Recruitment",
  "Onboarding",
  "Hiring & Onboarding",
  "Employee Engagement",
  "Remote Work",
  "Time Off & PTO",
  "Code of Conduct",
  "Diversity & Inclusion",
  "Performance Management",
  "Benefits & Compensation",
  "Health & Safety",
  "Professional Development",
  "Communication",
  "Workplace Technology",
  "Legal & Compliance",
  "Other",
] as const;

export const REPORT_REASONS: { value: ReportReason; label: string }[] = [
  { value: "spam", label: "Spam" },
  { value: "harassment", label: "Harassment or Abuse" },
  { value: "misinformation", label: "Misinformation" },
  { value: "impersonation", label: "Impersonation" },
  { value: "inappropriate", label: "Inappropriate Content" },
  { value: "other", label: "Other" },
];

// ─── Feedback ─────────────────────────────────────────────────────────────────

export async function submitFeedback(payload: FeedbackPayload): Promise<void> {
  await api.post("/api/community/feedback/", payload);
}

// ─── Reports ──────────────────────────────────────────────────────────────────

export async function submitReport(payload: ReportPayload): Promise<void> {
  await api.post("/api/community/reports/", payload);
}

// ─── Document submissions ─────────────────────────────────────────────────────

export async function submitDocument(formData: FormData): Promise<void> {
  await api.post("/api/community/submissions/", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
}

// ─── Notifications ────────────────────────────────────────────────────────────

export async function getNotifications(): Promise<Notification[]> {
  const res = await api.get<Notification[]>("/api/community/notifications/");
  return res.data;
}

export async function getUnreadCount(): Promise<number> {
  const res = await api.get<{ unread_count: number }>(
    "/api/community/notifications/unread-count/"
  );
  return res.data.unread_count;
}

export async function markNotificationRead(id: string): Promise<void> {
  await api.patch(`/api/community/notifications/${id}/read/`);
}

export async function markAllNotificationsRead(): Promise<void> {
  await api.patch("/api/community/notifications/mark-all-read/");
}

// ─── Announcements ────────────────────────────────────────────────────────────

export interface Announcement {
  id: string;
  title: string;
  preview: string;
  body: string;
  created_at: string;
}

export async function getAnnouncements(): Promise<Announcement[]> {
  const res = await api.get<Announcement[]>("/api/community/announcements/");
  return res.data;
}

export async function dismissAnnouncement(id: string): Promise<void> {
  await api.post(`/api/community/announcements/${id}/dismiss/`);
}

// ─── Follow ───────────────────────────────────────────────────────────────────

export async function getFollowStatus(handleId: string): Promise<boolean> {
  const res = await api.get<{ is_following: boolean }>(`/api/docs/follows/${handleId}/`);
  return res.data.is_following;
}

export async function followHandle(handleId: string): Promise<void> {
  await api.post(`/api/docs/follows/${handleId}/`);
}

export async function unfollowHandle(handleId: string): Promise<void> {
  await api.delete(`/api/docs/follows/${handleId}/`);
}
